import { useEffect, useState } from "react";
import { Alert, Form, Input, Modal, Select, Typography } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { NC_SEV, isNC, type AnswerResult, type AuditDetailDto, type QuestionDto } from "@audit-plan/shared";
import { usePeople } from "../../api/people";
import { invalidateAudit } from "../../api/audits";
import { FilePicker } from "../evidence/FilePicker";
import { UploadHint, UploadQueueList } from "../evidence/UploadQueueList";
import { useUploadQueue } from "../evidence/useUploadQueue";
import { useFullModal } from "./mobileModal";

/** Modale per risposte NC_* e OSS: commento obbligatorio, responsabile (NC), allegato opzionale. */
export function AnswerModal(props: {
  open: boolean;
  onClose: () => void;
  audit: AuditDetailDto;
  question: QuestionDto | null;
  result: AnswerResult | null;
  busy?: boolean;
  /** Salva la risposta; true se salvata. Poi la modale carica gli allegati in coda e si chiude da sola. */
  onSubmit: (v: { comment: string; ownerId?: string }) => Promise<boolean>;
}) {
  const { open, onClose, audit, question: q, result: r, busy, onSubmit } = props;
  const { t, i18n } = useTranslation();
  const en = i18n.language === "en";
  const people = usePeople();
  const [comment, setComment] = useState("");
  const [ownerId, setOwnerId] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const full = useFullModal();
  const qc = useQueryClient();
  const queue = useUploadQueue();
  const prev = q ? audit.answers[q.id] : undefined;
  const nc = q ? audit.ncs.find((n) => n.questionId === q.id && n.status !== "closed") : undefined;

  useEffect(() => {
    if (!open) return;
    setComment(prev?.comment ?? "");
    setOwnerId(nc?.ownerId ?? audit.auditorId);
    setSaved(false);
    setErr("");
    queue.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, q?.id, r]);

  if (!q || !r) return null;
  const neg = isNC(r);
  const uploading = queue.busy;
  const failed = queue.items.some((x) => x.status === "error");
  const run = async () => {
    setErr("");
    if (!saved) {
      const ok = await onSubmit({ comment: comment.trim(), ownerId: neg ? ownerId : undefined });
      if (!ok) return;
      setSaved(true);
    }
    if (!queue.items.some((x) => x.status !== "done")) return onClose();
    const res = await queue.upload({ supplierId: audit.supplierId, auditId: audit.id, target: { type: "question", id: q.id } });
    invalidateAudit(qc, audit.id);
    if (res.ok) onClose();
    else setErr(t("b_up_some_failed"));
  };
  return (
    <Modal
      {...full}
      open={open}
      onCancel={uploading ? queue.cancel : onClose}
      title={`${t(`ans_${r}`)} · ${q.code}`}
      okText={saved && failed && !uploading ? t("b_up_retry") : saved && !failed ? t("b_close") : t("save")}
      cancelText={uploading ? t("b_up_cancel") : saved ? t("b_close") : t("cancel")}
      cancelButtonProps={saved && !uploading ? { style: { display: "none" } } : undefined}
      confirmLoading={busy || uploading}
      okButtonProps={{ disabled: !comment.trim() }}
      onOk={run}
      closable={!uploading}
      maskClosable={false}
      keyboard={!uploading}
      destroyOnHidden
    >
      <Typography.Paragraph strong style={{ marginBottom: 4 }}>
        {en ? q.en || q.it : q.it}
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary">{q.area}</Typography.Paragraph>
      <Form layout="vertical">
        <Form.Item label={`${t("comment")} *`} required>
          <Input.TextArea
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={r === "OSS" ? (en ? "Observation" : "Osservazione") : en ? "Finding: what was found, where, evidence seen" : "Rilievo: cosa è stato trovato, dove, evidenze viste"}
          />
        </Form.Item>
        {neg ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Form.Item label={t("severity")} extra={en ? "Taken from the answer" : "Presa dall'esito della domanda"}>
              <Input value={t(`sev_${NC_SEV[r]}`)} disabled />
            </Form.Item>
            <Form.Item label={t("owner")}>
              <Select value={ownerId} onChange={setOwnerId} options={people.options(["auditor", "manager", "admin"])} showSearch optionFilterProp="label" />
            </Form.Item>
          </div>
        ) : null}
        <Form.Item label={t("evidence")}>
          <FilePicker disabled={uploading} onFiles={queue.add} />
          <div style={{ marginTop: 6 }}>
            <UploadHint />
          </div>
          <UploadQueueList items={queue.items} busy={uploading} onRemove={queue.remove} onRetry={run} />
        </Form.Item>
      </Form>
      {err ? <Alert type="error" showIcon message={err} style={{ marginBottom: 8 }} /> : null}
      <Alert
        type="info"
        showIcon
        message={
          r === "OSS"
            ? en
              ? "An observation is opened (no non-conformity). The question keeps its points."
              : "Si apre un'osservazione, non una non conformità. La domanda mantiene i punti."
            : en
              ? "A non-conformity is opened with this classification; corrective actions are defined later in the CAP."
              : "Si apre una non conformità con questa classificazione; le azioni correttive si definiscono dopo, nel CAP."
        }
      />
    </Modal>
  );
}
